import type { Container, Operation } from '@packages/core/container';

import { Ssm5Client } from '@packages/clients';

import type { Ssm5RecentConnection } from '../types';

import { toRecentConnection } from '../helpers/mappers';

/** Parameters of {@link GetRecentConnection}. */
export type GetRecentConnectionParams = {
    /** Aborts the lookup. */
    signal?: AbortSignal;
    /** The VIN whose connections are searched. */
    vin: string;
};

/** Look up the most recent eligible SSM5 connection of a VIN, reusable to reconnect its vehicle. */
export class GetRecentConnection implements Operation {
    /**
     * Register this operation with the SSM5 client as its dependency.
     *
     * @param container - the container to register in
     */
    static registerInContainer(container: Container): void {
        container.register(GetRecentConnection, [Ssm5Client]);
    }

    /** @param client - the SSM5 API client */
    constructor(private client: Ssm5Client) {}

    /**
     * Run the lookup.
     *
     * @param params - {@link GetRecentConnectionParams}
     * @returns the most recent connection, or undefined when the VIN has none; rejects when aborted
     */
    async exec({ signal, vin }: GetRecentConnectionParams): Promise<Ssm5RecentConnection | undefined> {
        const record = await this.client.getRecentConnectionByVin({ vin }, signal);

        return record && toRecentConnection(record);
    }
}
